import React, { useContext } from 'react';
import styled from 'styled-components';
import { Transition } from 'react-spring/renderprops';
import { Select, Input, InputNumber, DatePicker, Card } from 'components';
import { ListingLabelContext } from 'contexts';
import {
  CreateListingCardProps,
  StatusPriceChangeFields,
  ListingType,
  AvailabilityDetailsType,
} from '../types';
import SmallPreview from './SmallPreview';
import { StyledGrid, StyledAlert } from './StyledComponents';

type StatusPriceChangeProps =
  CreateListingCardProps<StatusPriceChangeFields> & {
    validAvailabilities: ListingType['availability'][];
    availabilityDetails: AvailabilityDetailsType;
    setAvailabilityDetails: React.Dispatch<
      React.SetStateAction<AvailabilityDetailsType>
    >;
    initialAvailabilityDetails: AvailabilityDetailsType;
    errorMessage?: string;
  };

const StatusPriceChange: React.FC<StatusPriceChangeProps> = ({
  thumbnail,
  line1,
  line2,
  city,
  province,
  postal_code,
  list_price,
  availability,
  setListing,
  errorMessage,
  validAvailabilities,
  availabilityDetails: {
    newAvailability,
    suspended_date,
    closed_date,
    sold_date,
    leased_date,
    leased_term,
    price,
    coop_brokerage,
    coop_salesperson_1,
    coop_salesperson_2,
    condition,
    condition_expiry,
  },
  setAvailabilityDetails,
  initialAvailabilityDetails,
}) => {
  const { getLabel, getAvailability, isHidden } =
    useContext(ListingLabelContext);

  const PriceChange = (
    <InputNumber
      label="New Price"
      type="currency"
      required
      value={price}
      onChange={(value: number) =>
        setAvailabilityDetails(prev => ({ ...prev, price: value }))
      }
      data-cy="listing-input-new-price"
    />
  );

  const SuspendedDate = (
    <DatePicker
      key="SuspendedDate"
      label="Suspended Date"
      required
      value={suspended_date}
      onChange={value =>
        setAvailabilityDetails(prev => ({
          ...prev,
          suspended_date: value,
        }))
      }
      format="MMM D, YYYY"
    />
  );

  const Condition = (
    <Input
      key="Condition"
      label="Condition"
      required
      value={condition}
      onChange={({ target: { value } }) =>
        setAvailabilityDetails(prev => ({ ...prev, condition: value }))
      }
      data-cy="listing-sold-conditionally-condition"
    />
  );

  const ConditionExpiry = (
    <DatePicker
      key="ConditionExpiry"
      label="Condition Expiry Date"
      required
      value={condition_expiry}
      onChange={value =>
        setAvailabilityDetails(prev => ({
          ...prev,
          condition_expiry: value,
        }))
      }
      format="MMM D, YYYY"
      data-cy="listing-sold-conditionally-condition-expiry"
    />
  );

  const SoldLeasedPrice = (
    <InputNumber
      key="SoldLeasedPrice"
      label={newAvailability === 'Sold' ? 'Sold Price' : 'Leased Price'}
      type="currency"
      required
      value={price}
      onChange={(value: number) =>
        setAvailabilityDetails(prev => ({ ...prev, price: value }))
      }
      data-cy={`listing-${
        newAvailability === 'Sold' ? 'sold' : 'leased'
      }-price`}
    />
  );

  const SoldLeasedDates = (
    <StyledGrid $columns={2} key="SoldLeasedDates">
      {newAvailability === 'Sold' ? (
        <DatePicker
          label="Sold Date"
          required
          value={sold_date}
          onChange={value =>
            setAvailabilityDetails(prev => ({
              ...prev,
              sold_date: value,
            }))
          }
          format="MMM D, YYYY"
          data-cy="listing-sold-date"
        />
      ) : (
        <DatePicker
          label="Leased Date"
          required
          value={leased_date}
          onChange={value =>
            setAvailabilityDetails(prev => ({
              ...prev,
              leased_date: value,
            }))
          }
          format="MMM D, YYYY"
          data-cy="listing-leased-date"
        />
      )}
      <DatePicker
        label="Closed Date"
        required
        value={closed_date}
        onChange={value =>
          setAvailabilityDetails(prev => ({
            ...prev,
            closed_date: value,
          }))
        }
        format="MMM D, YYYY"
        data-cy={`listing-${
          newAvailability === 'Sold' ? 'sold' : 'leased'
        }-closed-date`}
      />
    </StyledGrid>
  );

  const Coops = [
    <Input
      key="Co-op Brokerage"
      label="Buyer Agent's Brokerage"
      required
      value={coop_brokerage}
      onChange={({ target: { value } }) =>
        setAvailabilityDetails(prev => ({
          ...prev,
          coop_brokerage: value,
        }))
      }
      data-cy={`listing-${
        newAvailability === 'Sold' ? 'sold' : 'leased'
      }-coop-brokerage`}
    />,
    <Input
      key="Co-op Agent 1"
      label="Buyer Agent 1"
      required
      value={coop_salesperson_1}
      onChange={({ target: { value } }) =>
        setAvailabilityDetails(prev => ({
          ...prev,
          coop_salesperson_1: value,
        }))
      }
      data-cy={`listing-${
        newAvailability === 'Sold' ? 'sold' : 'leased'
      }-coop-salesperson1`}
    />,
    <Input
      key="Co-op Agent 2"
      label="Buyer Agent 2"
      value={coop_salesperson_2}
      onChange={({ target: { value } }) =>
        setAvailabilityDetails(prev => ({
          ...prev,
          coop_salesperson_2: value,
        }))
      }
      data-cy={`listing-${
        newAvailability === 'Sold' ? 'sold' : 'leased'
      }-coop-salesperson2`}
    />,
  ];

  const LeasedTerm = (
    <Input
      key="LeasedTerm"
      label="Leased Term"
      required
      value={leased_term}
      onChange={({ target: { value } }) =>
        setAvailabilityDetails(prev => ({
          ...prev,
          leased_term: value,
        }))
      }
    />
  );

  const getConditionalItems = () => {
    const fieldsMap: Record<
      NonNullable<AvailabilityDetailsType['newAvailability']>,
      JSX.Element[]
    > = {
      'Price Change': [PriceChange],
      Suspended: [SuspendedDate],
      Terminated: [],
      Pending: [],
      'Temp Off Market': [],
      Sold: [SoldLeasedPrice, SoldLeasedDates, ...Coops],
      Leased: [SoldLeasedPrice, SoldLeasedDates, ...Coops, LeasedTerm],
      'Sold Conditionally': [Condition, ConditionExpiry],
      'Leased Conditionally': [Condition, ConditionExpiry],
    };
    if (!newAvailability) return [];
    return fieldsMap[newAvailability];
  };

  const editable = !['Terminated', 'Suspended'].includes(availability!);

  return (
    <StyledCard type="primary" title={`Change ${getLabel('Status')}/Price`}>
      {errorMessage && <StyledAlert showIcon message={errorMessage} />}
      <SmallPreview
        thumbnail={thumbnail}
        line1={line1}
        line2={line2}
        city={city}
        province={province}
        postal_code={postal_code}
        list_price={list_price}
      />
      <StyledGridContainer>
        <StyledGrid $columns={1} $rowGap="0">
          <Select
            label={`Update ${getLabel('Status')}/Price Change`}
            allowClear
            value={editable ? newAvailability : availability}
            onChange={value =>
              setAvailabilityDetails(() => ({
                ...initialAvailabilityDetails,
                price: undefined,
                newAvailability: value,
              }))
            }
            disabled={!editable}
            options={validAvailabilities
              .filter(avail => !isHidden(avail))
              .map(avail => ({
                label: getAvailability(avail),
                value: avail,
              }))}
          />
          <Transition
            items={getConditionalItems()}
            keys={getConditionalItems().map(item => item.key)}
            from={{ opacity: 0, height: 0, marginTop: 0, overflow: 'hidden' }}
            enter={{
              opacity: 1,
              height: 54,
              marginTop: 10,
              overflow: 'visible',
            }}
            leave={{ opacity: 0, height: 0, marginTop: 0, overflow: 'hidden' }}
          >
            {item => props => <div style={props}>{item}</div>}
          </Transition>
        </StyledGrid>
      </StyledGridContainer>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  margin: auto;
`;

const StyledGridContainer = styled.div`
  margin-top: 20px;
`;

export default React.memo(StatusPriceChange);
